import { CONFIG } from '@frameio/config';
import type {
  PlanFeatureInput,
  PlanTier,
} from '@frameio/graphql-client/generated';
import type { PopoverProps } from '@frameio/vapor';
import { theme } from '@frameio/vapor';
import { IconStarFantasyFill } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/config';

TEXT.DEFINE({
  /** Title for custom-branded shares feature */
  customHeadersTitle: 'Custom-Branded Shares',
  /** Description explaining custom-branded shares feature */
  customHeadersDescription:
    'Personalize your Share with a custom background image, logo, and description, and choose to display or hide creator info.',
  /** Title for comment attachments feature */
  commentAttachmentsTitle: 'Comment Attachments',
  /** Description explaining comment attachments feature */
  commentAttachmentsDescription:
    'Add more context to your comments by attaching images, videos, and documents.',
  /** Title for reel layout feature */
  reelLayoutTitle: 'Reel Layout',
  /** Description explaining reel layout feature */
  reelLayoutDescription:
    'Showcase your content in its best light with a Reel layout that puts assets front and center above a seamless navigational carousel.',
});

const configText = {
  get customHeadersTitle() {
    return TEXT.customHeadersTitle;
  },
  get customHeadersDescription() {
    return TEXT.customHeadersDescription;
  },
  get commentAttachmentsTitle() {
    return TEXT.commentAttachmentsTitle;
  },
  get commentAttachmentsDescription() {
    return TEXT.commentAttachmentsDescription;
  },
  get reelLayoutTitle() {
    return TEXT.reelLayoutTitle;
  },
  get reelLayoutDescription() {
    return TEXT.reelLayoutDescription;
  },
};

// Enterprise hover/active background. Color in Figma not found in Vapor
// https://www.figma.com/design/TS8r8Pb542NYBjcu8sY0hf/%E2%9C%85-Dynamic-Feature-Gates?node-id=5354-74251&t=QevU3nu6XmuH2Xy6-0
const ENTERPRISE_BRAND_BLUE = '#4979FA';

const proTierConfig = {
  icon: IconStarFantasyFill,
  color: theme.colors.brand.primary,
  colorLowOpacity: theme.colors.background.interactive.brandLow,
  // the color of the text when the chip is not hovered
  defaultTextColor: theme.colors.text.link,
};

type FeatureGateTierConfig = {
  [key in PlanTier]: {
    color: string;
    colorLowOpacity: string;
    defaultTextColor: string;
    icon: React.ComponentType<{
      style?: React.CSSProperties;
    }>;
  };
};

const featureGateTierConfig: FeatureGateTierConfig = {
  BUSINESS: proTierConfig,
  ENTERPRISE: {
    ...proTierConfig,
    color: ENTERPRISE_BRAND_BLUE,
    colorLowOpacity: theme.colors.meta.lowOpacity.blue,
    defaultTextColor: theme.colors.meta.text.blue,
  },
  FREE: proTierConfig,
  PRO: proTierConfig,
  STARTER: proTierConfig,
  TEAM: {
    ...proTierConfig,
    color: theme.colors.meta.opaque.purple,
    colorLowOpacity: theme.colors.meta.lowOpacity.purple,
    defaultTextColor: theme.colors.meta.text.purple,
  },
};

const defaultTier = 'PRO';

export function getTierConfig(tier: PlanTier = defaultTier) {
  return featureGateTierConfig[tier] || featureGateTierConfig[defaultTier];
}

type PopoverContent = {
  description: string;
  image: string;
  imageHeight: string;
  imageWidth: string;
  offset?: PopoverProps['offset'];
  showBackgroundBlur?: boolean;
  title: string;
};

type FeatureGatePopoverContent = {
  [key in PlanFeatureInput]?: PopoverContent;
};

const getDefaultFeatureGatePopoverContent = (): PopoverContent => ({
  description: configText.customHeadersDescription,
  image: `${CONFIG.staticUrl}/feature-gate/branded-header3.png`,
  imageHeight: '185px',
  imageWidth: '448px',
  showBackgroundBlur: true,
  title: configText.customHeadersTitle,
});

const getFeatureGatePopoverContentMap = (): FeatureGatePopoverContent => ({
  COMMENT_ATTACHMENTS: {
    description: configText.commentAttachmentsDescription,
    image: `${CONFIG.staticUrl}/feature-gate/comment-attachment6.png`,
    imageHeight: '242px',
    imageWidth: '328px',
    showBackgroundBlur: false,
    title: configText.commentAttachmentsTitle,
  },
  CUSTOM_BRANDED_SHARES: getDefaultFeatureGatePopoverContent(),
  REEL_PLAYER: {
    description: configText.reelLayoutDescription,
    image: `${CONFIG.staticUrl}/feature-gate/reel-layout2.png`,
    imageHeight: '334px',
    imageWidth: '448px',
    offset: [65, -4],
    showBackgroundBlur: true,
    title: configText.reelLayoutTitle,
  },
});

export function getFeatureGatePopoverContent(
  feature: PlanFeatureInput = 'CUSTOM_BRANDED_SHARES'
): PopoverContent {
  const featureGatePopoverContent = getFeatureGatePopoverContentMap();
  return (
    featureGatePopoverContent[feature] ?? getDefaultFeatureGatePopoverContent()
  );
}

// what keys should trigger the click event
export const onKeyDownKeys = new Set(['Enter', ' ']);

export enum SubscriptionUpdateSource {
  BILLING_PAGE_ADD_CARD = 'BillingPageAddCard',
  FEATURE_GATE = 'FeatureGate',
  GUEST_LIMIT_BLOCK = 'GuestLimitBlock',
  MEMBER_LIMIT_BLOCK = 'MemberLimitBlock',
  OVER_LIMIT_BANNER = 'OverLimitBanner',
  PAUSE_OFFER = 'PauseOffer',
  PLAN_SELECTION = 'PlanSelection',
  PROJECT_LIMIT_BLOCK = 'ProjectLimitBlock',
  STORAGE_ADDONS = 'StorageAddOns',
  STORAGE_LIMIT_BLOCK = 'StorageLimitBlock',
  TRIAL_ENDING = 'TrialEnding',
  WORKSPACE_LIMIT_BLOCK = 'WorkspaceLimitBlock',
  SAVE_OFFER = 'SaveOffer',
}

export enum UsageLimitExceeded {
  GUEST = 'GuestLimit',
  MEMBER = 'MemberLimit',
  PROJECT = 'ProjectLimit',
  STORAGE = 'StorageLimit',
  WORKSPACE = 'WorkspaceLimit',
}

type SegmentTrackingValues = {
  features: {
    [key in PlanFeatureInput]?: string;
  };
  types: {
    popover: string;
    tooltip: string;
    userAction: string;
  };
};

export const segmentTrackingValues: SegmentTrackingValues = {
  features: {
    ALLOWED_DOMAINS: 'AllowedDomains',
    ARCHIVAL_STORAGE: 'ArchivalStorage',
    ASSET_LIFECYCLE_MANAGEMENT: 'AssetLifecycleManagement',
    COMMENT_ATTACHMENTS: 'CommentAttachments',
    CUSTOM_BRANDED_EMAILS: 'CustomBrandedEmails',
    CUSTOM_BRANDED_PRESENTATIONS: 'CustomBrandedPresentations',
    CUSTOM_BRANDED_SHARES: 'CustomBrandedShares',
    CUSTOM_SORT: 'CustomSort',
    DEVICES: 'Devices',
    DISABLE_SHARE_COMMENTS: 'DisableShareComments',
    DISABLE_SHARE_DOWNLOADS: 'DisableShareDownloads',
    DISABLE_SHARE_PREVIOUS_VERSIONS: 'DisableSharePreviousVersions',
    DISABLE_TEAM_MEMBER_INVITES: 'DisableTeamMemberInvites',
    DRM: 'DRM',
    FORENSIC_WATERMARKING: 'ForensicWatermarking',
    FOUR_K_PLAYBACK: 'FourKPlayback',
    HDR: 'Hdr',
    MOUNTED_STORAGE: 'MountedStorage',
    MOUNTED_STORAGE_OVERPROVISIONING_ALLOWED:
      'MountedStorageOverprovisioningAllowed',
    NATURAL_LANGUAGE_SEARCH: 'NaturalLanguageSearch',
    PASSWORD_PROTECTED_SHARES: 'PasswordProtectedShares',
    PRIVATE_PROJECTS: 'RestrictedProjects',
    REEL_PLAYER: 'ReelPlayer',
    RESOURCE_CONTROL_POLICIES: 'ResourceControlPolicies',
    RESTRICTED_FOLDERS: 'RestrictedFolders',
    SECURE_SHARING: 'SecureSharing',
    SEMANTIC_SEARCH: 'SemanticSearch',
    SESSION_BASED_WATERMARKING: 'SessionBasedWatermarking',
    SHARE_LINK_EXPIRATION: 'ShareLinkExpiration',
    SHARE_LISTS: 'ShareLists',
    STATIC_WATERMARK: 'StaticWatermark',
    STORAGE_CONNECT: 'StorageConnect',
    TEAM_ONLY_COMMENTS: 'TeamOnlyComments',
    TRANSCRIPTION: 'Transcription',
    UNLIMITED_WATERMARK_TEMPLATES: 'UnlimitedWatermarkTemplates',
    UPLOAD_LINKS: 'UploadLinks',
    USER_GROUPS: 'UserGroups',
    USER_MANAGEMENT: 'UserManagement',
  },
  types: {
    popover: 'Popover',
    tooltip: 'Tooltip',
    userAction: 'UserAction',
  },
};

export const themeProviderBrand = 'fio';

export const TOOLTIP_TRACKING_DELAY_MS = 300;
